import { TablePagination } from '@/components/pagination/TablePagination';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useReportStore from '../../store/report';
import { createColumns } from './columns';

export const ReportsTable: FC = () => {
  const { t } = useTranslation();
  const columns = createColumns();
  const { reports, reportsIsLoading, reportsPagination } = useReportStore();
  const [searchParams] = useSearchParams();

  const pageSize = Number(searchParams.get('size')) || 10;
  const currentPage = Number(searchParams.get('page')) || 1;

  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-md border bg-white overflow-hidden">
      <ScrollArea className="w-full">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="text-xs font-medium text-gray-800 border-b border-gray-200"
              >
                <TableHead className="w-[50px]">№</TableHead>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="whitespace-wrap w-50 p-2"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}

            <TableRow className="bg-gray-100 font-semibold text-gray-900 border-b">
              <TableHead className="text-left">{t('total')}</TableHead>
              {columns.map((column) => {
                const columnDef = column as { accessorKey?: string };
                const accessorKey = columnDef.accessorKey || '';
                const totalValue = reports?.total?.[accessorKey];

                return (
                  <TableHead
                    key={accessorKey || column.id}
                    className="whitespace-wrap w-50 p-2"
                  >
                    {totalValue !== undefined && totalValue !== null
                      ? totalValue
                      : ''}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>

          <TableBody>
            {reportsIsLoading ? (
              [...Array(10)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell className="h-[52px] text-center">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-8 mx-auto" />
                  </TableCell>
                  {columns.map((_, cellIndex) => (
                    <TableCell key={cellIndex} className="h-[52px]">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-light border-b border-gray-200 text-gray-600"
                >
                  <TableCell className="text-center text-black font-medium">
                    {index + 1}
                  </TableCell>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="h-[52px] min-w-[160px]">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-[52px] text-center"
                >
                  {t('no.data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" className="h-4" />
      </ScrollArea>

      {reportsPagination?.totalElements === 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(
            (reportsPagination?.totalElements || 0) / pageSize,
          )}
          totalElements={reportsPagination?.totalElements}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};
